import React, { Component } from "react";
import axios from "../../utils/axiosInstance";
import Swal from "sweetalert2";
import "./Admins.css";

class Admins extends Component {
  state = {
    admins: [],
    formData: {
      id: null,
      fullname: "",
      username: "",
      password: "",
      status: "1",
      role_name: "Admin",
      pages: [],
      image: null,
      image_preview: null,
    },
    modalMode: "add",
    deleteId: null,
    loading: false,
    submitting: false,
    deleting: false,
    error: "",
    showCheckboxes: false,
    baseUrl: axios.defaults.baseURL.replace("/api", ""),
    
    // Filters & Pagination
    searchTerm: "",
    statusFilter: "all",
    roleFilter: "all",
    currentPage: 1,
    itemsPerPage: 10,
  };

  pageFields = [
    "dashboard", "bookings", "posts", "customers", "coupons", "admins",
    "seo_pages", "expenses", "additional_page", "theme_customization", "settings"
  ];

  componentDidMount() {
    this.fetchAdmins();
    // Close multiselect if clicked outside
    document.addEventListener("mousedown", this.handleClickOutside);
  }

  componentWillUnmount() {
    document.removeEventListener("mousedown", this.handleClickOutside);
  }

  handleClickOutside = (event) => {
    if (this.multiselectRef && !this.multiselectRef.contains(event.target)) {
      this.setState({ showCheckboxes: false });
    }
  };

  resetForm = () => {
    this.setState({
      formData: {
        id: null,
        fullname: "",
        username: "",
        password: "",
        status: "1",
        role_name: "Admin",
        pages: [],
        image: null,
        image_preview: null,
      },
      modalMode: "add",
      submitting: false,
      error: "",
      showCheckboxes: false,
    });
  };

  fetchAdmins = async () => {
    this.setState({ loading: true, error: "" });
    try {
      const token = localStorage.getItem("token") || "";
      const res = await axios.get("/admins", {
        headers: { Authorization: token ? `Bearer ${token}` : "" },
      });
      const data = res.data?.data || [];
      this.setState({ 
        admins: Array.isArray(data) ? data : [],
        currentPage: 1 
      });
    } catch (err) {
      this.setState({
        error: err.response?.data?.message || "Failed to load admins.",
      });
    } finally {
      this.setState({ loading: false });
    }
  };

  handleChange = (e) => {
    const { name, value, files } = e.target;

    if (name === "image" && files?.length) {
      this.setState((prev) => ({
        formData: {
          ...prev.formData,
          image: files[0],
          image_preview: URL.createObjectURL(files[0]),
        },
      }));
      return;
    }

    this.setState((prev) => ({
      formData: { ...prev.formData, [name]: value },
      error: ""
    }));
  };

  handlePageCheckbox = (e) => {
    const value = e.target.value;
    const isChecked = e.target.checked;

    this.setState((prev) => {
      let pages = [...prev.formData.pages];
      if (isChecked) pages.push(value);
      else pages = pages.filter((v) => v !== value);

      return { formData: { ...prev.formData, pages } };
    });
  };

  // --- Filtering & Pagination Methods ---
  handleSearchChange = (e) => {
    this.setState({ searchTerm: e.target.value, currentPage: 1 });
  };

  handleFilterChange = (e) => {
    const { name, value } = e.target;
    this.setState({ [name]: value, currentPage: 1 });
  };

  handleItemsPerPageChange = (e) => {
    this.setState({ itemsPerPage: Number(e.target.value), currentPage: 1 });
  };

  clearFilters = () => {
    this.setState({ searchTerm: "", statusFilter: "all", roleFilter: "all", currentPage: 1 });
  };

  getFilteredAdmins = () => {
    const { admins, searchTerm, statusFilter, roleFilter } = this.state;
    const keyword = searchTerm.trim().toLowerCase();

    return admins.filter((admin) => {
      const name = (admin.fullname || "").toLowerCase();
      const username = (admin.username || "").toLowerCase();
      const role = admin.role_name || "";
      const status = admin.status?.toString() || "0";

      const matchesSearch = !keyword || name.includes(keyword) || username.includes(keyword);
      const matchesStatus = statusFilter === "all" || status === statusFilter;
      const matchesRole = roleFilter === "all" || role === roleFilter;

      return matchesSearch && matchesStatus && matchesRole;
    });
  };

  getPageNumbers = (totalPages) => {
    const { currentPage } = this.state;
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, index) => index + 1);
    }

    const pages = [1];
    if (currentPage > 4) pages.push("...");

    const start = Math.max(2, currentPage - 1);
    const end = Math.min(totalPages - 1, currentPage + 1);

    for (let page = start; page <= end; page += 1) {
      pages.push(page);
    }

    if (currentPage < totalPages - 3) pages.push("...");
    pages.push(totalPages);

    return pages;
  };

  goToPage = (page) => {
    const { currentPage } = this.state;
    const totalPages = Math.max(1, Math.ceil(this.getFilteredAdmins().length / this.state.itemsPerPage));
    let nextPage = page;

    if (page === "prev") nextPage = currentPage - 1;
    if (page === "next") nextPage = currentPage + 1;
    if (typeof nextPage !== "number") return;

    nextPage = Math.min(totalPages, Math.max(1, nextPage));
    if (nextPage !== currentPage) this.setState({ currentPage: nextPage });
  };

  // --- Modals & Submission ---
  openModal = (mode = "add", admin = null) => {
    const modalElement = document.getElementById("adminModal");
    if (!modalElement || !window.bootstrap) return;
    const modal = new window.bootstrap.Modal(modalElement);

    if (mode === "edit" && admin) {
      const pages = this.pageFields.filter(page => admin[page] === "1" || admin[page] === 1);

      this.setState({
        modalMode: "edit",
        formData: {
          id: admin.id,
          fullname: admin.fullname || "",
          username: admin.username?.replace("admin_", "") || admin.username || "",
          password: "",
          status: admin.status?.toString() || "1",
          role_name: admin.role_name || "Admin",
          pages: pages,
          image: null,
          image_preview: admin.image ? `${this.state.baseUrl}/uploads/admins/${admin.image}` : null,
        },
        error: ""
      });
    } else {
      this.resetForm();
    }
    modal.show();
  };

  closeModal = () => {
    const modalElement = document.getElementById("adminModal");
    if (modalElement && window.bootstrap) {
      const modal = window.bootstrap.Modal.getInstance(modalElement);
      if (modal) modal.hide();
    }
    this.resetForm();
  };

  handleSubmit = async (e) => {
    e.preventDefault();
    const { formData, modalMode } = this.state;

    if (!formData.fullname || !formData.username) {
      return this.setState({ error: "Please fill all required fields." });
    }

    this.setState({ submitting: true, error: "" });

    try {
      const token = localStorage.getItem("token") || "";
      const headers = {
        Authorization: token ? `Bearer ${token}` : "",
        "Content-Type": "multipart/form-data",
      };

      const payload = new FormData();
      payload.append("fullname", formData.fullname);
      payload.append("username", formData.username);
      payload.append("status", formData.status);
      payload.append("role_name", formData.role_name);
      payload.append("pages", JSON.stringify(formData.pages));

      if (formData.password) payload.append("password", formData.password);
      if (formData.image) payload.append("image", formData.image);

      if (modalMode === "add") {
        await axios.post("/admins", payload, { headers });
      } else {
        await axios.post(`/admins/${formData.id}?_method=PUT`, payload, { headers });
      }

      await this.fetchAdmins();
      this.closeModal();

      Swal.fire({
        icon: "success",
        title: modalMode === "add" ? "Admin added successfully!" : "Admin updated successfully!",
        timer: 1500,
        showConfirmButton: false,
      });
    } catch (err) {
      this.setState({
        error: err.response?.data?.message || "Check input fields or unique username",
      });
    } finally {
      this.setState({ submitting: false });
    }
  };

  confirmDelete = (id) => {
    this.setState({ deleteId: id });
    const modalElement = document.getElementById("deleteAdminModal");
    if (!modalElement || !window.bootstrap) return;
    const modal = new window.bootstrap.Modal(modalElement);
    modal.show();
  };

  handleDelete = async () => {
    const { deleteId } = this.state;
    if (!deleteId) return;

    this.setState({ deleting: true });
    try {
      const token = localStorage.getItem("token") || "";
      const form = new URLSearchParams();
      form.append("_method", "DELETE");

      await axios.post(`/admins/${deleteId}`, form, {
        headers: { Authorization: `Bearer ${token}` },
      });

      await this.fetchAdmins();

      const modalElement = document.getElementById("deleteAdminModal");
      if (modalElement && window.bootstrap) {
        const modal = window.bootstrap.Modal.getInstance(modalElement);
        if (modal) modal.hide();
      }
      this.setState({ deleteId: null });

      Swal.fire({
        icon: "success",
        title: "Admin deleted successfully!",
        timer: 1500,
        showConfirmButton: false,
      });
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Failed to delete!",
        text: err.response?.data?.message || err.message,
      });
    } finally {
      this.setState({ deleting: false });
    }
  };

  renderAvatarUploader() {
    const { image_preview } = this.state.formData;
    return (
      <div className="admin-avatar-upload-container">
        {image_preview ? (
          <img src={image_preview} alt="Avatar" className="admin-avatar-preview" />
        ) : (
          <div className="admin-avatar-placeholder-lg">
            <i className="ph ph-user"></i>
          </div>
        )}
        <label className="admin-avatar-overlay">
          <input
            type="file"
            name="image"
            onChange={this.handleChange}
            accept="image/*"
            className="d-none"
          />
          <i className="ph ph-camera"></i>
          <span>Upload</span>
        </label>
      </div>
    );
  }

  render() {
    const { 
      admins, formData, error, loading, submitting, deleting, modalMode, 
      baseUrl, showCheckboxes, searchTerm, statusFilter, roleFilter, 
      currentPage, itemsPerPage 
    } = this.state;

    const filteredAdmins = this.getFilteredAdmins();
    const totalPages = Math.max(1, Math.ceil(filteredAdmins.length / itemsPerPage));
    const safeCurrentPage = Math.min(currentPage, totalPages);
    const startIndex = (safeCurrentPage - 1) * itemsPerPage;
    const currentAdmins = filteredAdmins.slice(startIndex, startIndex + itemsPerPage);
    const startRecord = filteredAdmins.length ? startIndex + 1 : 0;
    const endRecord = Math.min(startIndex + itemsPerPage, filteredAdmins.length);
    
    const activeCount = admins.filter((a) => a.status?.toString() === "1").length;
    const inactiveCount = admins.length - activeCount;
    const pageNumbers = this.getPageNumbers(totalPages);

    return (
      <div className="pc-container admins-page">
        <div className="pc-content">
          <div className="admins-shell">
            {/* Topbar */}
            <div className="admins-topbar">
              <div>
                <div className="admins-breadcrumb">
                  <span>Management</span>
                  <i className="ph ph-caret-right" />
                  <strong>Admins</strong>
                </div>
                <h2 className="admins-title">Admin Management</h2>
                <p className="admins-subtitle">
                  Manage administrative accounts, roles, and platform access.
                </p>
              </div>

              <button
                type="button"
                className="bookly-primary-btn"
                onClick={() => this.openModal("add")}
              >
                <i className="ph ph-plus" />
                <span>New Admin</span>
              </button>
            </div>

            {/* Summary */}
            <div className="admin-summary-grid">
              <div className="admin-summary-card">
                <div className="admin-summary-icon blue">
                  <i className="ph ph-users" />
                </div>
                <div>
                  <span className="summary-label">Total Admins</span>
                  <strong>{admins.length}</strong>
                </div>
              </div>

              <div className="admin-summary-card">
                <div className="admin-summary-icon green">
                  <i className="ph ph-user-check" />
                </div>
                <div>
                  <span className="summary-label">Active Accounts</span>
                  <strong>{activeCount}</strong>
                </div>
              </div>

              <div className="admin-summary-card">
                <div className="admin-summary-icon purple">
                  <i className="ph ph-shield-star" />
                </div>
                <div>
                  <span className="summary-label">Inactive / Suspended</span>
                  <strong>{inactiveCount}</strong>
                </div>
              </div>
            </div>

            {/* Main Table Card */}
            <div className="admin-card">
              <div className="admin-card-header">
                <div>
                  <h4>Administrators List</h4>
                  <p>
                    {filteredAdmins.length} result{filteredAdmins.length !== 1 ? "s" : ""} found
                  </p>
                </div>
              </div>

              {/* Toolbar */}
              <div className="admin-toolbar">
                <div className="admin-search-wrap">
                  <i className="ph ph-magnifying-glass" />
                  <input
                    type="text"
                    className="admin-search"
                    placeholder="Search by name or username..."
                    value={searchTerm}
                    onChange={this.handleSearchChange}
                  />
                  {searchTerm && (
                    <button
                      type="button"
                      className="admin-search-clear"
                      onClick={() => this.setState({ searchTerm: "", currentPage: 1 })}
                    >
                      <i className="ph ph-x" />
                    </button>
                  )}
                </div>

                <div className="admin-filter-group">
                  <label htmlFor="roleFilter">Role</label>
                  <select
                    id="roleFilter"
                    name="roleFilter"
                    className="admin-filter-select"
                    value={roleFilter}
                    onChange={this.handleFilterChange}
                  >
                    <option value="all">All Roles</option>
                    <option value="Admin">Admin</option>
                    <option value="Manager">Manager</option>
                    <option value="Editor">Editor</option>
                  </select>
                </div>

                <div className="admin-filter-group">
                  <label htmlFor="statusFilter">Status</label>
                  <select
                    id="statusFilter"
                    name="statusFilter"
                    className="admin-filter-select"
                    value={statusFilter}
                    onChange={this.handleFilterChange}
                  >
                    <option value="all">All Status</option>
                    <option value="1">Active</option>
                    <option value="0">Inactive</option>
                  </select>
                </div>

                <div className="admin-filter-group">
                  <label htmlFor="itemsPerPage">Rows</label>
                  <select
                    id="itemsPerPage"
                    className="admin-filter-select"
                    value={itemsPerPage}
                    onChange={this.handleItemsPerPageChange}
                  >
                    <option value="5">5</option>
                    <option value="10">10</option>
                    <option value="20">20</option>
                    <option value="50">50</option>
                  </select>
                </div>

                {(searchTerm || statusFilter !== "all" || roleFilter !== "all") && (
                  <button
                    type="button"
                    className="admin-reset-btn"
                    onClick={this.clearFilters}
                  >
                    <i className="ph ph-arrow-counter-clockwise" />
                    Reset
                  </button>
                )}
              </div>

              {error && !loading && (
                <div className="admin-alert">
                  <i className="ph ph-warning-circle" />
                  <span>{error}</span>
                  <button type="button" onClick={this.fetchAdmins}>Retry</button>
                </div>
              )}

              {loading ? (
                <div className="admin-loading">
                  <div className="spinner-border" role="status" />
                  <span>Loading administrators...</span>
                </div>
              ) : (
                <>
                  <div className="admin-table-wrap">
                    <table className="admin-table">
                      <thead>
                        <tr>
                          <th className="col-number">#</th>
                          <th className="col-profile">Profile</th>
                          <th className="col-username">Username</th>
                          <th className="col-role">Role</th>
                          <th className="col-date">Last Login</th>
                          <th className="col-date">Registered</th>
                          <th className="col-status">Status</th>
                          <th className="col-actions">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {currentAdmins.length ? (
                          currentAdmins.map((a, i) => {
                            const isActive = a.status?.toString() === "1";
                            return (
                              <tr key={a.id}>
                                <td className="col-number table-index">
                                  {startIndex + i + 1}
                                </td>
                                <td>
                                  <div className="admin-profile-cell">
                                    {a.image ? (
                                      <img
                                        src={`${baseUrl}/uploads/admins/${a.image}`}
                                        alt={a.fullname}
                                        className="admin-avatar-img"
                                      />
                                    ) : (
                                      <div className="admin-avatar-placeholder">
                                        <i className="ph ph-user" />
                                      </div>
                                    )}
                                    <span className="admin-name">{a.fullname}</span>
                                  </div>
                                </td>
                                <td>
                                  <span style={{ color: "#475569", fontWeight: "600" }}>
                                    @{a.username?.replace("admin_", "")}
                                  </span>
                                </td>
                                <td>
                                  <span className="admin-role-badge">{a.role_name}</span>
                                </td>
                                <td>
                                  <span style={{ color: "#64748b", fontSize: "12px" }}>
                                    {a.last_login_date || "Never"}
                                  </span>
                                </td>
                                <td>
                                  <span style={{ color: "#64748b", fontSize: "12px" }}>
                                    {a.registration_date || "N/A"}
                                  </span>
                                </td>
                                <td>
                                  <span className={`admin-status-badge ${isActive ? "active" : "inactive"}`}>
                                    <span className="status-dot" />
                                    {isActive ? "Active" : "Inactive"}
                                  </span>
                                </td>
                                <td className="col-actions">
                                  <div className="admin-actions">
                                    <button
                                      className="admin-action-btn edit"
                                      onClick={() => this.openModal("edit", a)}
                                      title="Edit admin"
                                    >
                                      <i className="ph ph-note-pencil" />
                                    </button>
                                    <button
                                      className="admin-action-btn delete"
                                      onClick={() => this.confirmDelete(a.id)}
                                      title="Delete admin"
                                    >
                                      <i className="ph ph-trash" />
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            );
                          })
                        ) : (
                          <tr>
                            <td colSpan="8">
                              <div className="admin-empty-state">
                                <div className="empty-icon">
                                  <i className="ph ph-users" />
                                </div>
                                <h5>No administrators found</h5>
                                <p>
                                  {searchTerm || statusFilter !== "all" || roleFilter !== "all"
                                    ? "Try changing your search or filters."
                                    : "Add your first administrator to get started."}
                                </p>
                                {searchTerm || statusFilter !== "all" || roleFilter !== "all" ? (
                                  <button type="button" className="admin-reset-btn" onClick={this.clearFilters}>
                                    Clear Filters
                                  </button>
                                ) : (
                                  <button type="button" className="bookly-secondary-btn" onClick={() => this.openModal("add")}>
                                    <i className="ph ph-plus" />
                                    Add Admin
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  {filteredAdmins.length > 0 && (
                    <div className="admin-pagination-wrap">
                      <div className="admin-pagination-info">
                        Showing <strong>{startRecord}</strong> to <strong>{endRecord}</strong> of{" "}
                        <strong>{filteredAdmins.length}</strong> administrators
                      </div>

                      {totalPages > 1 && (
                        <ul className="admin-pagination">
                          <li className={safeCurrentPage === 1 ? "disabled" : ""}>
                            <button onClick={() => this.goToPage("prev")} disabled={safeCurrentPage === 1}>
                              <i className="ph ph-caret-left" />
                            </button>
                          </li>
                          {pageNumbers.map((page, index) =>
                            page === "..." ? (
                              <li key={`ellipsis-${index}`} className="ellipsis"><span>...</span></li>
                            ) : (
                              <li key={page} className={safeCurrentPage === page ? "active" : ""}>
                                <button onClick={() => this.goToPage(page)}>{page}</button>
                              </li>
                            )
                          )}
                          <li className={safeCurrentPage === totalPages ? "disabled" : ""}>
                            <button onClick={() => this.goToPage("next")} disabled={safeCurrentPage === totalPages}>
                              <i className="ph ph-caret-right" />
                            </button>
                          </li>
                        </ul>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        {/* Add/Edit Modal */}
        <div className="modal fade" id="adminModal" tabIndex={-1}>
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content admin-modal">
              <form onSubmit={this.handleSubmit}>
                <div className="modal-header admin-modal-header">
                  <div>
                    <span className="admin-modal-kicker">
                      {modalMode === "add" ? "Create" : "Update"}
                    </span>
                    <h5 className="modal-title">
                      {modalMode === "add" ? "Add Administrator" : "Edit Administrator"}
                    </h5>
                  </div>
                  <button
                    type="button"
                    className="admin-modal-close"
                    onClick={this.closeModal}
                  >
                    <i className="ph ph-x" />
                  </button>
                </div>

                <div className="modal-body admin-modal-body">
                  {error && (
                    <div className="admin-form-alert">
                      <i className="ph ph-warning-circle" />
                      <span>{error}</span>
                    </div>
                  )}

                  {this.renderAvatarUploader()}

                  <div className="row g-3">
                    <div className="col-md-6">
                      <div className="admin-form-group mb-0">
                        <label>Full Name <span>*</span></label>
                        <div className="admin-input-wrap">
                          <i className="ph ph-user" />
                          <input
                            type="text"
                            name="fullname"
                            value={formData.fullname}
                            onChange={this.handleChange}
                            className="admin-input"
                            placeholder="e.g. John Doe"
                            required
                          />
                        </div>
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="admin-form-group mb-0">
                        <label>Username <span>*</span></label>
                        <div className="admin-input-wrap">
                          <i className="ph ph-identification-card" />
                          <input
                            type="text"
                            name="username"
                            value={formData.username}
                            onChange={this.handleChange}
                            className="admin-input"
                            placeholder="e.g. johndoe99"
                            required
                          />
                        </div>
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="admin-form-group mb-0">
                        <label>Password {modalMode === "edit" && <span style={{color: '#94a3b8'}}>(Optional)</span>}</label>
                        <div className="admin-input-wrap">
                          <i className="ph ph-lock-key" />
                          <input
                            type="password"
                            name="password"
                            value={formData.password}
                            onChange={this.handleChange}
                            className="admin-input"
                            placeholder={modalMode === "edit" ? "Leave blank to keep current" : "Secure password"}
                            required={modalMode === "add"}
                          />
                        </div>
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="admin-form-group mb-0">
                        <label>Role</label>
                        <div className="admin-input-wrap">
                          <i className="ph ph-shield-check" />
                          <select
                            name="role_name"
                            value={formData.role_name}
                            onChange={this.handleChange}
                            className="admin-input admin-select"
                          >
                            <option value="Admin">Admin</option>
                            <option value="Manager">Manager</option>
                            <option value="Editor">Editor</option>
                          </select>
                        </div>
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="admin-form-group mb-0">
                        <label>Permissions (Pages)</label>
                        <div className="admin-multiselect" ref={(node) => (this.multiselectRef = node)}>
                          <div
                            className="admin-selectBox"
                            onClick={() => this.setState({ showCheckboxes: !showCheckboxes })}
                          >
                            <div className="admin-input-wrap">
                              <i className="ph ph-files" />
                              <input 
                                type="text" 
                                className="admin-input" 
                                value={`${formData.pages.length} Pages Selected`} 
                                readOnly 
                              />
                            </div>
                            <div className="admin-overSelect"></div>
                          </div>

                          {showCheckboxes && (
                            <div className="admin-checkboxes">
                              {this.pageFields.map((page) => (
                                <label key={page}>
                                  <input
                                    type="checkbox"
                                    value={page}
                                    checked={formData.pages.includes(page)}
                                    onChange={this.handlePageCheckbox}
                                  />
                                  {page.replace("_", " ").toUpperCase()}
                                </label>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="admin-form-group mb-0">
                        <label>Status</label>
                        <div className="admin-status-options">
                          <label className={`status-option ${formData.status === "1" ? "selected" : ""}`}>
                            <input
                              type="radio"
                              name="status"
                              value="1"
                              checked={formData.status === "1"}
                              onChange={this.handleChange}
                            />
                            <span><i className="ph ph-check-circle" /> Active</span>
                          </label>

                          <label className={`status-option ${formData.status === "0" ? "selected" : ""}`}>
                            <input
                              type="radio"
                              name="status"
                              value="0"
                              checked={formData.status === "0"}
                              onChange={this.handleChange}
                            />
                            <span><i className="ph ph-pause-circle" /> Inactive</span>
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="modal-footer admin-modal-footer">
                  <button
                    type="button"
                    className="bookly-cancel-btn"
                    onClick={this.closeModal}
                    disabled={submitting}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="bookly-primary-btn"
                    disabled={submitting}
                  >
                    {submitting ? (
                      <><span className="spinner-border spinner-border-sm" /> Saving...</>
                    ) : (
                      <><i className={modalMode === "add" ? "ph ph-plus" : "ph ph-check"} /> {modalMode === "add" ? "Add Admin" : "Update Admin"}</>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>

        {/* Delete Modal */}
        <div className="modal fade" id="deleteAdminModal" tabIndex={-1}>
          <div className="modal-dialog modal-dialog-centered modal-sm">
            <div className="modal-content delete-modal">
              <div className="delete-modal-body">
                <div className="delete-warning-icon">
                  <i className="ph ph-trash" />
                </div>
                <h5>Delete Administrator?</h5>
                <p>
                  This account will be permanently removed. This action cannot be undone.
                </p>
                <div className="delete-modal-actions">
                  <button
                    type="button"
                    className="bookly-cancel-btn"
                    data-bs-dismiss="modal"
                    disabled={deleting}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="bookly-danger-btn"
                    onClick={this.handleDelete}
                    disabled={deleting}
                  >
                    {deleting ? (
                      <><span className="spinner-border spinner-border-sm" /> Deleting...</>
                    ) : (
                      <><i className="ph ph-trash" /> Yes, Delete</>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default Admins;